import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, catchError, filter, take, throwError, timeout } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { NotificationService } from '@shared/services/notification.service';
import { esColaPdfLlena, esSesionVencida } from './errores-api.util';

/**
 * Rutas de la sesion misma. Un 401 aca (clave equivocada en el login, perfil sin
 * sesion al arrancar) no es una sesion vencida, y el logout que dispara este
 * interceptor pasa por aca: si se revisara, podria volver a dispararse.
 * Las demas rutas bajo /auth (ej. /auth/users) si se revisan.
 */
const RUTAS_DE_SESION = /\/auth\/(login|logout|profile|register)(\?|$)/;
const RUTA_LOGOUT = /\/auth\/logout(\?|$)/;
/** El logout con la conexion caida no debe dejar al usuario esperando para siempre. */
const TIEMPO_MAXIMO_LOGOUT_MS = 10000;
const AVISO_429_CADA_MS = 10000;

/**
 * Errores HTTP que la pantalla no sabe explicar sola (medido en Prod B el 2026-09-30):
 *
 * - Sesion vencida: el token dura 24 h y la API responde 403 'Forbidden for token'
 *   (401 si no llega token). No habia manejo: las listas cargaban vacias hasta que
 *   el usuario salia y volvia a entrar (unas 110 veces al dia). Ahora se avisa una
 *   sola vez, se limpia la sesion y se vuelve al login.
 * - 429: el limitador de la API corta las busquedas seguidas y los componentes lo
 *   tragaban mostrando una lista vacia. Ahora se avisa, a lo sumo cada 10 s.
 *
 * El error siempre se relanza: los componentes siguen con su propio manejo.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private cerrandoSesion = false;
  private ultimoAviso429 = 0;

  // AuthService y NotificationService se piden tarde, como en TokenInterceptor,
  // para no armar el ciclo AuthService -> HttpClient -> interceptores.
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (RUTAS_DE_SESION.test(req.url)) {
      return RUTA_LOGOUT.test(req.url)
        ? next.handle(req).pipe(timeout(TIEMPO_MAXIMO_LOGOUT_MS))
        : next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 429) {
            void this.avisarDemasiadasConsultas(error);
          } else if (error.status === 401 || error.status === 403) {
            void this.revisarSesion(error);
          }
        }
        return throwError(() => error);
      })
    );
  }

  private async avisarDemasiadasConsultas(error: HttpErrorResponse): Promise<void> {
    // La cola de PDF llena tambien responde 429, pero no es que el usuario consulto de
    // mas: ese aviso lo da la descarga (mensajeErrorDescargaPdf) y no gasta este cupo.
    if (await esColaPdfLlena(error)) return;
    const ahora = Date.now();
    if (ahora - this.ultimoAviso429 < AVISO_429_CADA_MS) return;
    this.ultimoAviso429 = ahora;
    this.injector
      .get(NotificationService)
      .warning('Demasiadas consultas seguidas. Espere unos segundos e intente de nuevo.');
  }

  private async revisarSesion(error: HttpErrorResponse): Promise<void> {
    if (this.cerrandoSesion) return;
    const authService = this.injector.get(AuthService);
    // Sin sesion abierta (pagina publica del QR, login) un 401 no es "sesion vencida".
    if (!this.estaLogueado(authService)) return;
    if (!(await esSesionVencida(error))) return;
    // Varios pedidos fallan juntos al vencer el token: avisa y cierra solo el primero.
    if (this.cerrandoSesion) return;
    this.cerrandoSesion = true;

    this.injector.get(NotificationService).warning('Su sesión venció. Inicie sesión de nuevo.');
    // Cuando la sesion local queda limpia, un vencimiento futuro (nuevo login) se vuelve a atender.
    authService.isLogged$
      .pipe(filter((logueado) => !logueado), take(1))
      .subscribe(() => (this.cerrandoSesion = false));
    // logout() limpia la sesion local aunque la API responda error (con el token vencido
    // responde 403) y navega a '/', que la ruta comodin manda a /auth/login. Su pedido
    // no se revisa aca (RUTAS_DE_SESION), asi que no hay bucle.
    authService.logout();
  }

  private estaLogueado(authService: AuthService): boolean {
    let logueado = false;
    authService.isLogged$.pipe(take(1)).subscribe((valor) => (logueado = valor));
    return logueado;
  }
}
